import React, { CSSProperties, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  useAddRejectionReason,
  useFarmingStagesByContractId,
  useGetContractById,
  useGetRejectionReasonsByContractById,
  useUpdateContractStatus,
  useUpdateRejectionStatus,
} from "../../api/contract";
import {
  Button,
  Card,
  Input,
  List,
  message,
  Modal,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import { AuthContextValue, useAuth } from "../../providers/AuthProvider";
import { Image } from "antd";
import {
  GoogleMap,
  InfoWindow,
  MarkerF,
  useLoadScript,
} from "@react-google-maps/api";

import img from "../../assets/images/user.png";
import female from "../../assets/images/user-female.png";
import { FarmLandCard, IAddress } from "../customer/CustomerDetailPage";
import OrderTrackingGraph from "../../components/OrderTrackingGraph";
import { CloudDownloadOutlined } from "@ant-design/icons";
import pdfIcon from "../../assets/images/pdf.png";
import { Key } from "lucide";
import LoadingScreen from "../../components/LoadingScreen";
import { Title } from "chart.js";

const container: CSSProperties = {
  fontFamily: "Poppins",
  borderRadius: 8.5,
  marginTop: 25,
  backgroundColor: "white",
  paddingTop: 28,
  paddingBottom: 38,
  paddingLeft: 75,
  paddingRight: 75,
};

const wrapper: CSSProperties = {
  display: "flex",
  width: "100%",
  gap: 10,
  fontSize: 16,
  // marginBottom: 20,
  // flexDirection: "column",
};
const label: CSSProperties = {
  color: "#128807",
  fontWeight: 500,
  fontSize: 20,
};
const value: CSSProperties = {
  color: "#8590A3",
  fontWeight: 500,
  fontSize: 16,
  alignItems: "center",
  display: "flex",
};

const getStatusColor = (status: string) => {
  let color = "#FFC107";
  switch (status) {
    case "SIGNED":
      color = "green";
      break;
    case "NOT STARTED":
      color = "red";
      break;
    default:
      color = "lightgray";
      break;
  }

  return color;
};

const getStatusText = (_status: number) => {
  let status = "NOT STARTED";
  switch (_status) {
    case 1:
      status = "SIGNED";
      break;
    case 2:
      status = "NOT STARTED";
      break;
  }
  return status;
};

const entitlementColumns = [
  {
    dataIndex: "inputName",
    title: "Input name",
  },
  {
    dataIndex: "quantity",
    title: "Quantity (Kg)",
  },
  {
    dataIndex: "unitPrice",
    title: "Unit Price",
  },
  {
    dataIndex: "totalPrice",
    title: "Total Price",
  },
  {
    dataIndex: "landSize",
    title: "Land Size (Hac)",
  },
  {
    dataIndex: "voucherStatus",
    title: "Status",
    render: (status: number) => {
      let _status = getStatusText(status);

      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            minWidth: 100,
            width: "80%",
            paddingTop: 6,
            paddingBottom: 6,
            borderRadius: 8.5,
            color: getStatusColor(_status),
            backgroundColor: `${getStatusColor(_status)}44`,
          }}
        >
          {_status}
        </div>
      );
    },
  },
];

const insuranceColumns = [
  {
    dataIndex: "companyName",
    title: "Company",
    render: (companyName: string, record: any) => (
      <div className="flex flex-col">
        <div>{companyName}</div>
        <div className="text-gray-500">{record.phoneNumber}</div>
      </div>
    ),
  },
  {
    dataIndex: "proposalNumber",
    title: "Proposal No.",
  },
  {
    dataIndex: "enrollmentDate",
    title: "Enrollment Date",
  },
  {
    dataIndex: "premium",
    title: "Premium (ETB)",
  },
  {
    dataIndex: "sumInsured",
    title: "Sum Insured",
  },
  {
    dataIndex: "status",
    title: "Status",
    render: (status: string) => {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            minWidth: 100,
            width: "80%",
            paddingTop: 6,
            paddingBottom: 6,
            borderRadius: 8.5,
            color: getStatusColor(status),
            backgroundColor: `${getStatusColor(status)}44`,
          }}
        >
          {status}
        </div>
      );
    },
  },
];



const ContractDetailPage = () => {
  const { TextArea } = Input;
  const [photo, setPhoto] = useState<string | null | undefined>();
  const [documents, setDocuments] = useState<
    | { docId: number; customerId: number; file: string; fileName: string; fileType: string }[]
    | null
    | undefined
  >([]);
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: "AIzaSyBG4pD21Xf2Mqq0kMUo2POHq_UrHooGQ18",
  });

  const { id } = useParams();
  const { user } = useAuth() as AuthContextValue;

  const [mapView, setMapView] = useState<boolean>(false);
  const [farmLands, setFarmLands] = useState<IAddress[]>([]);
  const [activeMarker, setActiveMarker] = useState("");
  const [openRejectionModal, setOpenRejectionModal] = useState(false);
  const [tableData, setTableData] = useState<
    {
      id: number;
      inputName: string;
      quantity: number;
      unitPrice: number;
      totalPrice: number;
      landSize: number;
      voucherStatus: number;
    }[]
  >([]);

  const [insuranceTableData, setInsuranceTableData] = useState<
    {
      companyName: string;
      phoneNumber: string;
      proposalNumber: string;
      enrollmentDate: string;
      premium: string;
      sumInsured: string;
      status: string;
    }[]
  >([]);
  const {
    data,
    isLoading: contractLoading,
    refetch: fetchContract,
  } = useGetContractById(id);

  const { mutateAsync: updateStatus, isPending } = useUpdateContractStatus();
  const {
    mutateAsync: updateRejectionStatus,
    isPending: isRejectionStatusUpdatePending,
  } = useUpdateRejectionStatus();
  const { mutateAsync: addRejectionReason, isPending: isAddRejectionPending } =
    useAddRejectionReason();

  const {
    data: allRejectionReasons,
    isLoading: rejectionLoading,
    refetch: fetchRejectionReasons,
  } = useGetRejectionReasonsByContractById(id);

  const [selectedRejectionStatusId, setSelectedRejectionStatusId] =
    useState(-1);
  const [rejectionHistory, setRejectionHistory] = useState<
    { id: number; reason: string; status: "ACTIVE" | "RESOLVED" }[]
  >([]);

  const [inActiveRejections, setInActiveRejections] = useState(0);
  const [rejectionReason, setRejectionReason] = useState("");

  const [expandedList, setExpandedList] = useState<
    { id: number; isExpanded: boolean }[]
  >([]);

  const { data: trackingData, refetch } = useFarmingStagesByContractId(id);

  const [graphData, setGraphData] = useState<any>([]);
  const [activeTab, setActiveTab] = useState("1");
  const [preview, setPreview] = useState<{ file: string; fileType: string } | null>(null);
  useEffect(() => {
    const val = rejectionHistory.reduce<{ active: number; inactive: number }>(
      (acc, item) => {
        const key = item.status.toLowerCase() as keyof {
          active: number;
          inactive: number;
        };
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      },
      { active: 0, inactive: 0 }
    );

    setInActiveRejections(val.inactive);
    const _expandedList: { id: number; isExpanded: boolean }[] =
      rejectionHistory.map(
        (item: {
          id: number;
          reason: string;
          status: "ACTIVE" | "RESOLVED";
        }) => {
          return { id: item.id, isExpanded: false };
        }
      );

    setExpandedList(_expandedList);
  }, [rejectionHistory]);

  useEffect(() => {
    if (
      allRejectionReasons &&
      allRejectionReasons.data &&
      allRejectionReasons.data.data
    ) {
      const reasons = allRejectionReasons.data.data;
      setRejectionHistory(reasons);
    }
  }, [allRejectionReasons]);

  useEffect(() => {
    if (id) {
      fetchContract();
      fetchRejectionReasons();
    }
  }, [id]);

  useEffect(() => {
    if (data && data.data && data.data.data) {
      //     setCenter(
      //         { lat: data.data.data.customerDetail.farmLand.latitude as number, lng: data.data.data.customerDetail.farmLand.longitude as number });
    }
    if (
      data &&
      data.data &&
      data.data.data &&
      data.data.data.customerDetail.farmerDocuments
    ) {
      const _photo = data.data.data.customerDetail.farmerDocuments.find(
        (item: any) => item.fileName.toLowerCase().indexOf("photo") > -1
      );
      if (_photo) {
        setPhoto(_photo.file);
      }

      const _documents = data.data.data.customerDetail.farmerDocuments.filter(
        (item: any) => !item.fileName.startsWith("photo")
      );

      if (_documents) {
        const d = _documents.map((item: any) => {
          return {
            docId: item.docId,
            file: item.file,
            fileName: item.fileName,
            fileType: item.fileType,
          };
        });

        setDocuments(d);
      }
    }

    if (data && data.data && data.data.data && data.data.data.entitlements) {
      const _tableData: {
        id: number;
        inputName: string;
        quantity: number;
        unitPrice: number;
        totalPrice: number;
        landSize: number;
        voucherStatus: number;
      }[] = data.data.data.entitlements.map((item: any) => {
        return {
          id: item.id,
          inputName: item.input.inputName,
          quantity: item.quantity,
          unitPrice: item.unitPrice.toLocaleString(),
          totalPrice: item.totalPrice.toLocaleString(),
          landSize: item.landSize,
          voucherStatus: item.voucherStatus,
        };
      });

      if (_tableData) {
        setTableData(_tableData);
      }
    }

    if (data && data.data && data.data.data && data.data.data.insurance) {
      const _data = {
        companyName: data.data.data.insurance.insuranceCompany.orgName,
        phoneNumber: data.data.data.insurance.insuranceCompany.phoneNo,
        proposalNumber: data.data.data.insurance.proposalNumber,
        enrollmentDate: data.data.data.insurance.enrollmentDate,
        premium: data.data.data.insurance.premium.toLocaleString(),
        sumInsured: data.data.data.insurance.sumInsured.toLocaleString(),
        status: data.data.data.insurance.enrolmentStatus,
      };

      setInsuranceTableData([_data]);
    }

    if (data && data.data && data.data.data && data.data.data.customerDetail) {
      if (
        data.data.data.customerDetail.farmLand &&
        data.data.data.customerDetail.farmLand.length > 0
      ) {
        const item = data.data.data.customerDetail?.farmLand[0];
        const land: IAddress = {
          latitude: item?.latitude,
          longitude: item?.longitude,
          landCode: item?.landCode,
          landSize: item?.landSize,
          region: "",
          zone: "",
          woreda: "",
          kebele: "",
          unionName: "",
          primaryCooperativeName: "",
        };

        if (item?.address) {
          const _address = JSON.parse(item.address);
          land.region = _address.region;
          land.woreda = _address.woreda;
          land.zone = _address.zone;
          land.kebele = _address.kebele;
          land.unionName = _address.unionName;
          land.primaryCooperativeName = _address?.primaryCooperativeName;
        }

        setFarmLands([land]);
      }
    }
  }, [data]);

  const entitlements = data?.data?.data?.entitlements;
  const totalEntitlementsPrice = entitlements?.reduce(
    (accumulator: number, entitlement: { totalPrice: number }) => {
      return accumulator + entitlement.totalPrice;
    },
    0
  );

  
  const handleLoanStatus = async (_status: number) => {
    if (id) {
      try {
        const result = await updateStatus({ id: Number(id), status: _status });

        if (result) {
          message.success("Status updated successfully");
        } else {
          message.error("Operation failed.");
        }
      } catch (err: any) {
        message.error("Operation failed.");
      } finally {
        // refetch();
      }
    }
  };

  const handleOnLoad = (map: any) => {
    const bounds = new google.maps.LatLngBounds();
    map.fitBounds(bounds);
  };

  const handleActiveMarker = (landCode: string) => {
    if (landCode === activeMarker) {
      return;
    }
    setActiveMarker(landCode);
  };

  const handleUpdateRejectionStatus = async (id: number, checked: boolean) => {
    try {
      const result = await updateRejectionStatus({ status: checked, id });

      if (result) {
        message.success("Status updated successfully");
        fetchContract();
      } else {
        message.error("Operation failed.");
      }
    } catch (err: any) {
      message.error("Operation failed.");
    } finally {
      fetchRejectionReasons();
    }
  };

  const handleAddRejectionReason = async () => {
    try {
      const result = await addRejectionReason({
        reason: rejectionReason,
        status: "ACTIVE",
        id: Number(id),
      });
      console.log("gg", data);
      if (result) {
        message.success("add rejection reason successfully");
        fetchContract();
      } else {
        message.error("Operation failed.");
      }
    } catch (err: any) {
      message.error("Operation failed.");
    } finally {
      setRejectionReason("");
      setOpenRejectionModal(false);
      fetchRejectionReasons();
    }
  };

  useEffect(() => {
    if (
      trackingData &&
      trackingData.data &&
      trackingData.data.data &&
      trackingData.data.data.data
    ) {
      const val = Object.entries(trackingData.data.data.data).map((v: any) => {
        return { date: v[0], ...v[1] };
      });
      if (val) {
        setGraphData(val);
      }
    }
  }, [trackingData]);

  useEffect(() => {
    if (id) {
      refetch();
    }
  }, [id]);

  const dobString = data?.data?.data?.customerDetail.customer.dateOfBirth;
  const dob = new Date(dobString);
  const formattedDate = dob.toLocaleDateString("en-GB");

  

  const formatValue = (value: { toLocaleString: () => any }) =>
    value ? value.toLocaleString() : "N/A";

  const columns = [
    {
      title: "File Name",
      dataIndex: "fileName",
      key: "fileName",
      render: (text: string) => text?.split(".")[0],
    },
    {
      title: "File Type",
      dataIndex: "fileType",
      key: "fileType",
    },
    {
      title: "Preview",
      dataIndex: "preview",
      key: "preview",
      render: (text: any, record: any) =>
        record.fileType?.includes("pdf") ? (
          <Image preview={false} src={pdfIcon} width={50} height={50} />
        ) : (
          <Image src={`data:image/jpeg;base64,${record.file}`} height={50} width={50} />
        ),
    },
    
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (text: any, record: any) =>
        record.fileType?.includes("pdf") ? (
          <Button type="link" href={`data:application/pdf;base64,${record.file}`} download={`${data?.data?.data?.customerDetail?.fullName}-${record.fileName.split(".")[0]}`}>
            Download
          </Button>
        ) : (
          <Image src={`data:image/jpeg;base64,${record.file}`} height={50} width={50} />
        ),
    },
  ];

  if (contractLoading || isPending) return <LoadingScreen />;

  const loanDetails = [
    {
      label: "Credit Amount (ETB)",
      value: totalEntitlementsPrice,
    },
    {
      label: "Input Credit (ETB)",
      value: totalEntitlementsPrice,
    },
    {
      label: "Insurance Credit (ETB)",
      value: data?.data?.data?.contract?.insuranceCredit?.toLocaleString() || "N/A",
    },
    {
      label: "Loan Status",
      value: (
        <Tag color={getStatusColor(data?.data?.data?.contract?.loanStatus)}>
          {data?.data?.data?.contract?.loanStatus}
        </Tag>
      ),
    },
  ];

  const tabItems = [
    // {
    //   key: "1",
    //   label: "CONTRACT DETAIL",
    //   children: (
    //     <>
    //       {data && data.data && data.data.data && data.data.data.contract && (

    //         <div className="flex flex-col md:flex-row gap-6  w-full">
    //           <Card className="bg-transparent w-full p-6 shadow-md">
    //             <div
    //               style={{
    //                 backgroundColor: "#E9EDF4",
    //                 padding: "15px",
    //                 borderRadius: "8.5px",
    //                 fontWeight: 500,
    //                 fontSize: "24px",
    //                 marginBottom: "30px",
    //                 color: "#334155",
    //               }}
    //             >
    //               Contract Detail
    //             </div>
    //             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
    //               <div style={wrapper}>
    //                 <div style={label}>Contract No</div>
    //                 <div style={value}>
    //                   {data.data.data.contract.contractNumber}
    //                 </div>
    //               </div>

    //               <div style={wrapper}>
    //                 <div style={label}>Contract Date</div>
    //                 <div style={value}>
    //                   {data.data.data.contract.contractDate}
    //                 </div>
    //               </div>

    //               <div style={wrapper}>
    //                 <div style={label}>Product</div>
    //                 <div style={value}>{data.data.data.contract.product}</div>
    //               </div>

    //               <div style={wrapper}>
    //                 <div style={label}>Contract Price (ETB)</div>
    //                 <div style={value}>
    //                   {formatValue(data.data.data.contract.contractPrice)}
    //                 </div>
    //               </div>

    //               <div style={wrapper}>
    //                 <div style={label}>Spot Price (ETB)</div>
    //                 <div style={value}>
    //                   {formatValue(data.data.data.contract.spotPrice)}
    //                 </div>
    //               </div>
    //               <div style={wrapper}>
    //                 <div style={label}>Quantity (Kg)</div>
    //                 <div style={value}>
    //                   {formatValue(data.data.data.contract.quantity)}
    //                 </div>
    //               </div>

    //               <div style={wrapper}>
    //                 <div style={label}>Harvest Date</div>
    //                 <div style={value}>
    //                   {data.data.data.contract.harvestDate}
    //                 </div>
    //               </div>

    //               <div style={wrapper}>
    //                 <div style={label}>Contract Status</div>
    //                 <div style={value}>
    //                   {data.data.data.contract.contractStatus}
    //                 </div>
    //               </div>
    //             </div>
    //           </Card>

    //           <Card className="bg-transparent w-full p-6 shadow-md">
    //             <div className="mb-6">
    //               <div
    //                 style={{
    //                   backgroundColor: "#E9EDF4",
    //                   padding: "15px",
    //                   borderRadius: "8.5px",
    //                   fontWeight: 500,
    //                   fontSize: "24px",
    //                   marginBottom: "30px",
    //                   color: "#334155",
    //                 }}
    //               >
    //                 Farming Status
    //               </div>
    //               <div style={wrapper} className="mb-4 ml-4">
    //                 <div style={label}>Farming Status</div>
    //                 <div style={value}>
    //                   {data.data.data.contract.farmingStageStatus}
    //                 </div>
    //               </div>
    //               <span className="text-gray-300 text-xs font-semibold ml-4">
    //                 Toggle to switch between aggregate and daily{" "}
    //               </span>
    //               <OrderTrackingGraph data={graphData} />
    //             </div>
    //           </Card>
    //         </div>
    //       )}
    //     </>
    //   ),
    // },
    {
      key: "1",
      label: "CONTRACT DETAIL",
      children: (
        <div>
          {data &&
            data.data &&
            data.data.data &&
            data.data.data.customerDetail &&
            !contractLoading && (
              <div
                style={{
                  fontFamily: "Poppins",
                  // padding: 20,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  borderRadius: 8.5,
                  backgroundColor: "white",
                  height: "100%",
                }}
              >
                {/* <div className="flex w-full mb-[25px]">
                  <div style={{ width: "100%" }}>
                    <div
                      style={{
                        fontFamily: "Poppins",
                        fontWeight: 700,
                        borderRadius: "8.5px",
                        backgroundColor: "white",
                        // paddingTop: "20px",
                      }}
                    >
                      <div
                        style={{
                          backgroundColor: "#E9EDF4",
                          padding: "15px",
                          borderRadius: "8.5px",
                          fontWeight: 500,
                          fontSize: "24px",
                          marginBottom: "30px",
                        }}
                      >
                        Farmer detail
                      </div>
                      <div className="flex justify-center"></div>
                      <div
                        style={{
                          position: "relative",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: 200,
                          marginBottom: 20,
                        }}
                      >
                        {photo && (
                          <Image
                            src={`data:image/jpeg;base64,${photo}`}
                            height={150}
                            width={150}
                            style={{ borderRadius: "50%", marginTop: 5 }}
                          />
                        )}
                        {!photo &&
                          !data.data?.customerDetail?.customer?.gender && (
                            <Image
                              preview={false}
                              src={img}
                              style={{
                                width: 150,
                                height: 150,
                                borderRadius: 100,
                              }}
                            />
                          )}
                        {!photo &&
                          data?.data?.customerDetail?.customer?.gender &&
                          data?.data?.customerDetail?.customer?.gender ===
                            "Male" && (
                            <Image
                              preview={false}
                              src={img}
                              style={{
                                width: 150,
                                height: 150,
                                borderRadius: 100,
                              }}
                            />
                          )}
                        {!photo &&
                          data?.data?.customerDetail?.customer?.gender &&
                          data?.data?.customerDetail?.customer?.gender ===
                            "Female" && (
                            <Image
                              preview={false}
                              src={female}
                              style={{
                                width: 150,
                                height: 150,
                                borderRadius: 100,
                              }}
                            />
                          )}
                      </div>
                      <div className="grid grid-cols-3  pl-5">
                        <div style={wrapper}>
                          <div style={label}>Full name</div>
                          <div style={value}>
                            {data.data.data.customerDetail.customer.fullName}
                          </div>
                        </div>

                        <div style={wrapper}>
                          <div style={label}>Gender</div>
                          <div style={value}>
                            {data.data.data.customerDetail.customer.gender}
                          </div>
                        </div>

                        <div style={wrapper}>
                          <div style={label}>Date of Birth</div>
                          <div style={value}>{formattedDate}</div>
                        </div>

                        <div style={wrapper}>
                          <div style={label}>Phone No.</div>
                          <div style={value}>
                            {data.data.data.customerDetail.customer.phoneNumber}
                          </div>
                        </div>

                        <div style={wrapper}>
                          <div style={label}>Account No.</div>
                          <div style={value}>
                            {data.data.data.customerDetail.customer.bankAccount}
                          </div>
                        </div>
                        <div style={wrapper}>
                          <div style={label}>TIN</div>
                          <div style={value}>
                            {data.data.data.customerDetail.customer.tinNumber}
                          </div>
                        </div>

                        <div style={wrapper}>
                          <div style={label}>ID</div>
                          <div style={value}>
                            {
                              data.data.data.customerDetail.customer
                                .identificationNumber
                            }
                          </div>
                        </div>

                        <div style={wrapper}>
                          <div style={label}>Primary Cooperative Name</div>
                          <div style={value}>
                            {data?.data?.data?.customerDetail?.customer
                              ?.primaryCooperativeName || "N/A"}
                          </div>
                        </div>

                        <div style={wrapper}>
                          <div style={label}>Union Name</div>
                          <div style={value}>
                            {data?.data?.data?.customerDetail?.customer
                              ?.unionName || "N/A"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div> */}
                <div className="flex mb-[25px] gap-6 w-full">
                  <div className="bg-white rounded-lg shadow-md p-4 flex w-full">
                    <div className="flex flex-col gap-4 w-full">
                      {" "}
                      {/* Ensure full width */}
                      <div
                        style={{
                          backgroundColor: "#E9EDF4",
                          padding: "15px",
                          borderRadius: "8.5px",
                          fontWeight: 500,
                          fontSize: "24px",
                          marginBottom: "30px",
                          color: "#334155",
                        }}
                        className="w-full" // Make the title full width
                      >
                        Contract Detail
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
                        <div style={wrapper}>
                          <div style={label}>Contract Date</div>
                          <div style={value}>
                            {data.data.data.contract.contractDate}
                          </div>
                        </div>
                        <div style={wrapper}>
                          <div style={label}>Product</div>
                          <div style={value}>
                            {data.data.data.contract.product}
                          </div>
                        </div>

                        <div style={wrapper}>
                          <div style={label}>Contract Price (ETB)</div>
                          <div style={value}>
                            {formatValue(data.data.data.contract.contractPrice)}
                          </div>
                        </div>

                        <div style={wrapper}>
                          <div style={label}>Quantity (Kg)</div>
                          <div style={value}>
                            {formatValue(data.data.data.contract.quantity)}
                          </div>
                        </div>

                        <div style={wrapper}>
                          <div style={label}>Spot Price (ETB)</div>
                          <div style={value}>
                            {formatValue(data.data.data.contract.spotPrice)}
                          </div>
                        </div>

                        <div style={wrapper}>
                          <div style={label}>Harvest Date</div>
                          <div style={value}>
                            {data.data.data.contract.harvestDate}
                          </div>
                        </div>
                        <div style={wrapper}>
                          <div style={label}>Contract No</div>
                          <div style={value}>
                            {data.data.data.contract.contractNumber}
                          </div>
                        </div>

                        <div style={wrapper}>
                          <div style={label}>Contract Status</div>
                          <Tag
                            className="p-1"
                            color={
                              data.data.data.contract.contractStatus
                                ?.trim()
                                .toLowerCase() === "signed"
                                ? "green"
                                : data.data.data.contract.contractStatus
                                    ?.trim()
                                    .toLowerCase() === "unsigned"
                                ? "red"
                                : "default"
                            }
                          >
                            {data.data.data.contract.contractStatus}
                          </Tag>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
        </div>
      ),
    },
    {
      key: "2",
      label: "FARM DETAILS",
      children: (
        <>
          {data &&
            data.data &&
            data.data.data &&
            data.data.data.customerDetail &&
            !contractLoading && (
              <div className="flex w-full mb-[25px] gap-6">
                {/* Left side: Farmer photo card */}
                <div className="bg-white rounded-lg shadow-md p-4 flex items-center justify-center w-1/3">
                  {photo ? (
                    <Image
                      src={`data:image/jpeg;base64,${photo}`}
                      height={150}
                      width={150}
                      className="rounded-full"
                      alt="Farmer"
                    />
                  ) : (
                    <Image
                      src={
                        data?.data?.customerDetail?.customer?.gender ===
                        "Female"
                          ? female
                          : img
                      }
                      height={150}
                      width={150}
                      className="rounded-full"
                      alt="Default Profile"
                    />
                  )}
                </div>

                {/* Right side: Farmer details */}
                <div className="bg-white rounded-lg shadow-md p-6 w-2/3">
                  <div className="bg-gray-200 p-4 rounded-lg font-medium text-lg mb-4">
                    Farmer Detail
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      {
                        label: "Full Name",
                        value: data.data.data.customerDetail.customer.fullName,
                      },
                      {
                        label: "Gender",
                        value: data.data.data.customerDetail.customer.gender,
                      },
                      { label: "Date of Birth", value: formattedDate },
                      {
                        label: "Phone No.",
                        value:
                          data.data.data.customerDetail.customer.phoneNumber,
                      },
                      {
                        label: "Account No.",
                        value:
                          data.data.data.customerDetail.customer.bankAccount,
                      },
                      {
                        label: "TIN",
                        value: data.data.data.customerDetail.customer.tinNumber,
                      },
                      {
                        label: "ID",
                        value:
                          data.data.data.customerDetail.customer
                            .identificationNumber,
                      },
                      {
                        label: "Primary Cooperative Name",
                        value:
                          data?.data?.data?.customerDetail?.customer
                            ?.primaryCooperativeName || "N/A",
                      },
                      {
                        label: "Union Name",
                        value:
                          data?.data?.data?.customerDetail?.customer
                            ?.unionName || "N/A",
                      },
                    ].map((item, index) => (
                      <div key={index}>
                        <div style={label}>{item.label}</div>
                        <div style={value}>{item.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
        </>
      ),
    },
    {
      key: "3",
      label: "FARM DOCUMENTS",
      children: (
        <div>
          {data &&
            data.data &&
            data.data.data &&
            data.data.data.customerDetail && (
              <div
                style={{
                  fontFamily: "Poppins",
                  padding: 20,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  borderRadius: 8.5,
                  backgroundColor: "white",
                }}
              >
                <div className="flex w-full mb-[25px]">
                  <div style={{ width: "100%" }}>
                    <div
                      style={{
                        fontFamily: "Poppins",
                        fontWeight: 700,
                        borderRadius: "8.5px",
                        backgroundColor: "white",
                        paddingTop: "33px",
                      }}
                    >
                      <div
                        style={{
                          backgroundColor: "#E9EDF4",
                          padding: "15px",
                          borderRadius: "8.5px",
                          fontWeight: 500,
                          fontSize: "24px",
                          marginBottom: "30px",
                        }}
                      >
                        Customer documents
                      </div>
                      {/* <div
                        className={`flex ${
                          documents && documents.length > 0
                            ? "sm:grid-cols-1 md:grid-cols-4"
                            : "flex justify-center items-center"
                        } gap-4`}
                      >
                        {documents && documents.length > 0 && (
                          <div className="w-full flex flex-wrap items-start gap-12">
                            {documents.map((item: any) => {
                              if (
                                item.fileName.toLowerCase().indexOf("photo") >
                                -1
                              ) {
                                return <></>;
                              }
                              return (
                                <div
                                  key={item.docId}
                                  style={{
                                    position: "relative",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 12,
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: 200,
                                  }}
                                >
                                  {item.fileType.indexOf("pdf") < 0 && (
                                    <Image
                                      src={`data:image/jpeg;base64,${item.file}`}
                                      height={200}
                                      width={200}
                                    />
                                  )}
                                  {item.fileType.indexOf("pdf") >= 0 && (
                                    <>
                                      <div
                                        id="pdf"
                                        style={{ position: "relative" }}
                                      >
                                        <Image
                                          preview={false}
                                          src={pdfIcon}
                                          width={170}
                                          height={200}
                                        />
                                        <a
                                          href={`data:application/pdf;base64,${item.file}`}
                                          download={`${
                                            data?.data?.data?.customerDetail
                                              ?.fullName
                                          }-${item.fileName.split(".")[0]}`}
                                        >
                                          <CloudDownloadOutlined
                                            style={{
                                              position: "absolute",
                                              top: 0,
                                              bottom: 0,
                                              right: "calc(50% - 20px)",
                                              fontSize: 40,
                                              color: "green",
                                              // left: "50%",
                                            }}
                                          />
                                        </a>
                                      </div>
                                    </>
                                  )}
                                  <div>{item.fileName.split(".")[0]}</div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {documents && documents.length === 0 && (
                          <div className="flex flex-col justify-center items-center gap-4 text-2xl text-gray-400 min-h-[300px]">
                            <svg
                              width="184"
                              height="152"
                              viewBox="0 0 184 152"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <title>empty image</title>
                              <g fill="none" fill-rule="evenodd">
                                <g transform="translate(24 31.67)">
                                  <ellipse
                                    fill-opacity=".8"
                                    fill="#F5F5F7"
                                    cx="67.797"
                                    cy="106.89"
                                    rx="67.797"
                                    ry="12.668"
                                  ></ellipse>
                                  <path
                                    d="M122.034 69.674L98.109 40.229c-1.148-1.386-2.826-2.225-4.593-2.225h-51.44c-1.766 0-3.444.839-4.592 2.225L13.56 69.674v15.383h108.475V69.674z"
                                    fill="#AEB8C2"
                                  ></path>
                                  <path
                                    d="M101.537 86.214L80.63 61.102c-1.001-1.207-2.507-1.867-4.048-1.867H31.724c-1.54 0-3.047.66-4.048 1.867L6.769 86.214v13.792h94.768V86.214z"
                                    fill="url(#linearGradient-1)"
                                    transform="translate(13.56)"
                                  ></path>
                                  <path
                                    d="M33.83 0h67.933a4 4 0 0 1 4 4v93.344a4 4 0 0 1-4 4H33.83a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z"
                                    fill="#F5F5F7"
                                  ></path>
                                  <path
                                    d="M42.678 9.953h50.237a2 2 0 0 1 2 2V36.91a2 2 0 0 1-2 2H42.678a2 2 0 0 1-2-2V11.953a2 2 0 0 1 2-2zM42.94 49.767h49.713a2.262 2.262 0 1 1 0 4.524H42.94a2.262 2.262 0 0 1 0-4.524zM42.94 61.53h49.713a2.262 2.262 0 1 1 0 4.525H42.94a2.262 2.262 0 0 1 0-4.525zM121.813 105.032c-.775 3.071-3.497 5.36-6.735 5.36H20.515c-3.238 0-5.96-2.29-6.734-5.36a7.309 7.309 0 0 1-.222-1.79V69.675h26.318c2.907 0 5.25 2.448 5.25 5.42v.04c0 2.971 2.37 5.37 5.277 5.37h34.785c2.907 0 5.277-2.421 5.277-5.393V75.1c0-2.972 2.343-5.426 5.25-5.426h26.318v33.569c0 .617-.077 1.216-.221 1.789z"
                                    fill="#DCE0E6"
                                  ></path>
                                </g>
                                <path
                                  d="M149.121 33.292l-6.83 2.65a1 1 0 0 1-1.317-1.23l1.937-6.207c-2.589-2.944-4.109-6.534-4.109-10.408C138.802 8.102 148.92 0 161.402 0 173.881 0 184 8.102 184 18.097c0 9.995-10.118 18.097-22.599 18.097-4.528 0-8.744-1.066-12.28-2.902z"
                                  fill="#DCE0E6"
                                ></path>
                                <g
                                  transform="translate(149.65 15.383)"
                                  fill="#FFF"
                                >
                                  <ellipse
                                    cx="20.654"
                                    cy="3.167"
                                    rx="2.849"
                                    ry="2.815"
                                  ></ellipse>
                                  <path d="M5.698 5.63H0L2.898.704zM9.259.704h4.985V5.63H9.259z"></path>
                                </g>
                              </g>
                            </svg>
                            No legal documents available.
                          </div>
                        )}
                      </div> */}
                      {/* <div className="flex w-full flex-col mb-[25px] gap-6">
                        <div className="bg-white rounded-lg shadow-md p-6">
                          <div className="bg-gray-200 p-4 rounded-lg font-medium text-lg mb-4">
                            Customer Documents
                          </div>
                          {documents && documents.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                              {documents.map((item) => {
                                if (
                                  item.fileName.toLowerCase().includes("photo")
                                )
                                  return null;

                                return (
                                  <div
                                    key={item.docId}
                                    className="flex flex-col items-center bg-gray-100 p-4 rounded-lg shadow cursor-pointer hover:shadow-lg transition"
                                    onClick={() => setPreview(item)}
                                  >
                                    {item.fileType.includes("pdf") ? (
                                      <div className="relative">
                                        <Image
                                          src={pdfIcon}
                                          width={100}
                                          height={120}
                                          alt="PDF Preview"
                                        />
                                        <a
                                          href={`data:application/pdf;base64,${item.file}`}
                                          download={`${
                                            data?.data?.data?.customerDetail
                                              ?.fullName
                                          }-${item.fileName.split(".")[0]}`}
                                          className="absolute top-0 right-2 text-green-600 text-2xl"
                                        >
                                          <CloudDownloadOutlined />
                                        </a>
                                      </div>
                                    ) : (
                                      <Image
                                        src={`data:image/jpeg;base64,${item.file}`}
                                        width={100}
                                        height={120}
                                        className="rounded-lg"
                                        alt="Document Preview"
                                      />
                                    )}
                                    <div className="mt-2 text-sm text-gray-700">
                                      {item.fileName.split(".")[0]}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="flex flex-col justify-center items-center text-gray-400 text-lg min-h-[300px]">
                              <svg
                                width="184"
                                height="152"
                                viewBox="0 0 184 152"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <title>empty image</title>
                                <g fill="none" fillRule="evenodd">
                                  <g transform="translate(24 31.67)">
                                    <ellipse
                                      fillOpacity=".8"
                                      fill="#F5F5F7"
                                      cx="67.797"
                                      cy="106.89"
                                      rx="67.797"
                                      ry="12.668"
                                    ></ellipse>
                                    <path
                                      d="M122.034 69.674L98.109 40.229c-1.148-1.386-2.826-2.225-4.593-2.225h-51.44c-1.766 0-3.444.839-4.592 2.225L13.56 69.674v15.383h108.475V69.674z"
                                      fill="#AEB8C2"
                                    ></path>
                                  </g>
                                </g>
                              </svg>
                              No documents available.
                            </div>
                          )}
                        </div>
                        {preview && (
                          <div
                            className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center"
                            onClick={() => setPreview(null)}
                          >
                            <div className="bg-white p-4 rounded-lg shadow-lg max-w-lg">
                              {preview.fileType.includes("pdf") ? (
                                <iframe
                                  src={`data:application/pdf;base64,${preview.file}`}
                                  className="w-full h-[500px]"
                                />
                              ) : (
                                <Image
                                  src={`data:image/jpeg;base64,${preview.file}`}
                                  width={400}
                                  height={500}
                                  className="rounded-lg"
                                  alt="Preview"
                                />
                              )}
                              <div className="text-center mt-4">
                                <button
                                  className="bg-red-500 text-white px-4 py-2 rounded"
                                  onClick={() => setPreview(null)}
                                >
                                  Close
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div> */}
                      <Table
                        columns={columns}
                        dataSource={documents || []}
                        rowKey="docId"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
        </div>
      ),
    },
    {
      key: "4",
      label: "FARM LAND",
      children: (
        <div className="rounded-xl overflow-hidden mt-4 pt-2">
          <div className="flex gap-4">
            <span className="text-gray-600 text-xs font-semibold ml-4 ">
              Toggle to view map{" "}
            </span>
            <Switch
              checked={mapView}
              checkedChildren="Text View"
              unCheckedChildren="Map View"
              defaultChecked
              onChange={(val: any) => setMapView(val)}
            />
          </div>

          {isLoaded && mapView && (
            <GoogleMap
              onLoad={handleOnLoad}
              mapContainerClassName={`w-full min-h-[500px] rounded-xl ${
                mapView ? "block" : "hidden"
              }`}
              center={{
                lat: farmLands[0]?.latitude,
                lng: farmLands[0]?.longitude,
              }}
              zoom={15}
            >
              {farmLands &&
                farmLands.map((_data: IAddress) => {
                  if (_data.landCode) {
                    return (
                      <MarkerF
                        key={`${_data.landCode}-${_data.latitude}-${_data.longitude}`}
                        position={{
                          lat: _data.latitude,
                          lng: _data.longitude,
                        }}
                        onClick={() => handleActiveMarker(`${_data.landCode}`)}
                      >
                        {activeMarker === _data.landCode ? (
                          <InfoWindow
                            options={{
                              zIndex: 9999,
                              maxWidth: 400,
                            }}
                            position={{
                              lat: _data.latitude,
                              lng: _data.longitude,
                            }}
                            onCloseClick={() => setActiveMarker("")}
                          >
                            <FarmLandCard
                              key={_data.landCode}
                              region={_data.region}
                              zone={_data.zone}
                              woreda={_data.woreda}
                              kebele={_data.kebele}
                              landSize={_data.landSize}
                            />
                          </InfoWindow>
                        ) : null}
                      </MarkerF>
                    );
                  } else {
                    return <div></div>;
                  }
                })}
            </GoogleMap>
          )}
          {!mapView && (
            <div
              className={`flex flex-col justify-start min-h-[500px] bg-white p-[15px] rounded-xl mt-7.5`}
            >
              {farmLands.map((item: IAddress, index: number) => (
                <>
                  <div
                    key={index}
                    className={`bg-[#E9EDF4] p-[15px] rounded-xl ${
                      index === 0 ? "mb-[30px]" : "mt-4 mb-[30px]"
                    }`}
                    style={{
                      fontWeight: 500,
                      fontSize: "24px",
                    }}
                  >
                    Address
                  </div>
                  <div className="flex flex-col md:flex-row flex-wrap md:gap-6 pl-5">
                    <div
                      className="flex flex-row justify-between md:gap-[10px] border-b-2 md:border-b-0 md:flex-col w-auto"
                      style={{ fontSize: 16, marginBottom: 20 }}
                    >
                      <div style={label}>Region</div>
                      <div style={value}>{item.region || "N/A"}</div>
                    </div>

                    <div
                      className="flex flex-row justify-between md:gap-[10px] border-b-2 md:border-b-0 md:flex-col w-auto"
                      style={{ fontSize: 16, marginBottom: 20 }}
                    >
                      <div style={label}>Zone</div>
                      <div style={value}>{item.zone || "N/A"}</div>
                    </div>

                    <div
                      className="flex flex-row justify-between md:gap-[10px] border-b-2 md:border-b-0 md:flex-col w-auto"
                      style={{ fontSize: 16, marginBottom: 20 }}
                    >
                      <div style={label}>Woreda</div>
                      <div style={value}>{item.woreda || "N/A"}</div>
                    </div>

                    <div
                      className="flex flex-row justify-between md:gap-[10px] border-b-2 md:border-b-0 md:flex-col w-auto"
                      style={{ fontSize: 16, marginBottom: 20 }}
                    >
                      <div style={label}>Kebele</div>
                      <div style={value}>{item.kebele || "N/A"}</div>
                    </div>

                    <div
                      className="flex flex-row justify-between md:gap-[10px] border-b-2 md:border-b-0 md:flex-col w-auto"
                      style={{ fontSize: 16, marginBottom: 20 }}
                    >
                      <div style={label}>Land size</div>
                      <div style={value}>{`${item.landSize} ha` || "N/A"}</div>
                    </div>

                    <div
                      className="flex flex-row justify-between md:gap-[10px] border-b-2 md:border-b-0 md:flex-col w-auto"
                      style={{ fontSize: 16, marginBottom: 20 }}
                    >
                      <div style={label}>PC</div>
                      <div style={value}>
                        {data?.data?.data?.customerDetail?.customer
                          ?.primaryCooperativeName || "N/A"}
                      </div>
                    </div>

                    <div
                      className="flex flex-row justify-between md:gap-[10px] border-b-2 md:border-b-0 md:flex-col w-auto"
                      style={{ fontSize: 16, marginBottom: 20 }}
                    >
                      <div style={label}>Union</div>
                      <div style={value}>
                        {data?.data?.data?.customerDetail?.customer
                          ?.unionName || "N/A"}
                      </div>
                    </div>

                    <div
                      className="flex flex-row justify-between md:gap-[10px] border-b-2 md:border-b-0 md:flex-col w-auto"
                      style={{ fontSize: 16, marginBottom: 20 }}
                    >
                      <div style={label}>Latitude</div>
                      <div style={value}>{item.latitude || "N/A"}</div>
                    </div>

                    <div
                      className="flex flex-row justify-between md:gap-[10px] border-b-2 md:border-b-0 md:flex-col w-auto"
                      style={{ fontSize: 16, marginBottom: 20 }}
                    >
                      <div style={label}>Longitude</div>
                      <div style={value}>{item.longitude || "N/A"}</div>
                    </div>
                  </div>
                </>
              ))}
            </div>
          )}
          <div>
            <div
              style={{
                backgroundColor: "#E9EDF4",
                padding: "15px",
                borderRadius: "8.5px",
                fontWeight: 500,
                fontSize: "24px",
                marginBottom: "30px",
                marginTop: "30px",
              }}
            >
              Farming Status
            </div>
            <span className="text-gray-600 text-xs font-semibold ml-8 ">
              Toggle to view aggregate{" "}
            </span>
            <OrderTrackingGraph data={graphData} />
          </div>
        </div>
      ),
    },
    {
      key: "5",
      label: "ENTITLEMENT & INSURANCE",
      children: (
        <div className="hidden md:block">
          <div
            style={{
              fontFamily: "Poppins",
              fontWeight: 700,
              borderRadius: "8.5px",
              backgroundColor: "white",
              paddingTop: "33px",
              paddingBottom: "33px",
              // paddingRight: 50,
              // paddingLeft: 50,
            }}
          >
            <div className="flex flex-col w-full mb-[25px] gap-6">
              <div className="bg-white rounded-lg shadow-md p-4 flex items-center justify-center">
                <div className="flex flex-col gap-4 w-full">
                  <div
                    style={{
                      backgroundColor: "#E9EDF4",
                      padding: "15px",
                      borderRadius: "8.5px",
                      fontWeight: 500,
                      fontSize: "24px",
                      marginBottom: "30px",
                    }}
                  >
                    Entitlement
                  </div>
                  <Table columns={entitlementColumns} dataSource={tableData} />
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex flex-col gap-4 w-full">
                  <div
                    style={{
                      backgroundColor: "#E9EDF4",
                      padding: "15px",
                      borderRadius: "8.5px",
                      fontWeight: 500,
                      fontSize: "24px",
                      marginBottom: "30px",
                    }}
                  >
                    Insurance
                  </div>
                  <Table
                    columns={insuranceColumns}
                    dataSource={insuranceTableData}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "7",
      label: "LOAN STATUS",
      children: (
        <>
          {data && data.data && data.data.data && data.data.data.contract && (
            <>
              <div className="bg-white rounded-lg  p-4 flex items-center justify-center mx-auto">
                <div className="flex w-full mb-[25px]">
                  <div className="w-full  p-6">
                    {/* Header - Loan Status */}
                    <div className="bg-[#E9EDF4] text-[#334155] font-semibold text-xl p-4 rounded-lg mb-6">
                      Loan Status
                    </div>

                    {/* Loan Details Grid */}
                    <List
                      dataSource={loanDetails}
                      renderItem={(item) => (
                        <List.Item
                          style={{ borderBottom: "1px solid #E5E7EB" }}
                        >
                          <div className="flex justify-between w-full">
                            <div style={label}>{item.label}</div>
                            <div style={value}>{item.value}</div>
                          </div>
                        </List.Item>
                      )}
                    />

                    {/* Loan Approval & Rejection Buttons */}
                    {user?.roleName !== "ROLE_KFT_SP_ADMIN" &&
                      user?.roleName !== "ROLE_KFT_SUPER_ADMIN" && (
                        <div className="flex justify-center gap-6 mt-6">
                          <Button
                            onClick={() => handleLoanStatus(1)}
                            disabled={
                              isPending ||
                              inActiveRejections !== 0 ||
                              data?.data.data.contract.loanStatus === "APPROVED"
                            }
                            loading={isPending}
                            type="primary"
                          >
                            Loan Approved
                          </Button>

                          <Button
                            onClick={() => handleLoanStatus(0)}
                            disabled={isPending || inActiveRejections === 0}
                            loading={isPending}
                          >
                            Loan Rejected
                          </Button>
                        </div>
                      )}
                  </div>
                </div>
              </div>

              <div className="flex flex-col items-center rounded-xl p-5 pt-[22px] bg-white">
                <div className="w-full flex justify-between items-center bg-[#E9EDF4] p-[15px] rounded-xl font-bold text-[24px] m-b-[30px]">
                  <span>Rejection Histories</span>
                  <Button
                    type="primary"
                    onClick={() => setOpenRejectionModal(true)}
                  >
                    + Add New
                  </Button>
                </div>

                <div className="w-full flex flex-col gap-2 mt-5">
                  {rejectionHistory.map(
                    (item: {
                      id: number;
                      reason: string;
                      status: "ACTIVE" | "RESOLVED";
                    }) => {
                      const isExpanded = expandedList.find(
                        (expandedItem: { id: number; isExpanded: boolean }) =>
                          expandedItem.id === item.id
                      )?.isExpanded;
                      return (
                        <div
                          key={item.id}
                          className="w-full flex justify-between items-center hover:bg-[#E9EDF4] px-2 py-2 border-b-[1px] hover:cursor-pointer"
                        >
                          <div className="w-3/4">
                            {!isExpanded && (
                              <>
                                {item.reason.substring(0, 100)}
                                {item.reason.length > 100 && <>...</>}
                                {item.reason.length > 100 && (
                                  <Button
                                    onClick={() => {
                                      const _expandedList = expandedList.filter(
                                        (_item) => item.id !== item.id
                                      );
                                      setExpandedList([
                                        ..._expandedList,
                                        { id: item.id, isExpanded: true },
                                      ]);
                                    }}
                                    type="text"
                                    // className="inline-block"
                                  >
                                    <span className="text-gray-400">
                                      Read more
                                    </span>
                                  </Button>
                                )}
                              </>
                            )}

                            {isExpanded && (
                              <>
                                {item.reason}
                                <Button
                                  type="text"
                                  className="text-gray-400"
                                  onClick={() => {
                                    const _expandedList = expandedList.filter(
                                      (_item) => item.id !== item.id
                                    );
                                    setExpandedList([
                                      ..._expandedList,
                                      { id: item.id, isExpanded: false },
                                    ]);
                                  }}
                                >
                                  Read less
                                </Button>
                              </>
                            )}
                          </div>
                          <Switch
                            checkedChildren="ACTIVE"
                            unCheckedChildren="RESOLVED"
                            defaultChecked={item.status === "ACTIVE"}
                            onChange={(checked: boolean) => {
                              setSelectedRejectionStatusId(item.id);
                              handleUpdateRejectionStatus(item.id, checked);
                            }}
                            loading={
                              selectedRejectionStatusId === item.id &&
                              isRejectionStatusUpdatePending
                            }
                          />
                        </div>
                      );
                    }
                  )}
                </div>
              </div>
            </>
          )}
        </>
      ),
    },
  ];
  return (
    <div className="flex flex-col gap-8 pt-6 min-h-[600px]">
      <Modal
        title={
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 600,
              fontSize: 27.43,
              color: "black",
            }}
          >
            Rejection Reason
          </div>
        }
        open={openRejectionModal}
        centered
        onOk={handleAddRejectionReason}
        okButtonProps={{
          disabled: rejectionReason.length === 0,
        }}
        onCancel={() => setOpenRejectionModal(false)}
        closable
        destroyOnClose={true}
      >
        <TextArea
          placeholder="Insert the rejection history"
          onChange={(e) => setRejectionReason(e.target.value)}
        />
      </Modal>

      <Tabs
        style={{
          background: "white",
          fontWeight: 700,
          minHeight: contractLoading ? "70vh" : "auto", 
          paddingLeft: 20,
        }}
        className="text-black"
        defaultActiveKey="1"
        items={tabItems}
        onChange={(key) => setActiveTab(key)}
      />
    </div>
  );
};

export default ContractDetailPage;
